import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanionCard, CompanionAvatar, RestAtShoreButton } from '@/components/innerworld';
import { useCouncil } from '@/hooks/useInnerWorld';

const REALM_NAMES = { 2: 'The Garden', 3: 'The Hollow', 4: 'The Hearth', 5: 'The Horizon' };

const serif = { fontFamily: 'Playfair Display, serif' };
const sans = { fontFamily: 'Jost, sans-serif' };

const MatchReviewCard = ({ review, onResolve }) => {
  const [busy, setBusy] = useState(false);
  const name = review.candidate?.name || 'a part';
  const matches = review.possible_matches || [];
  const fromSage = review.candidate?.source === 'sage_chat';

  const resolve = async (action, targetId) => {
    setBusy(true);
    try { await onResolve(review.id, action, targetId); } finally { setBusy(false); }
  };

  return (
    <div
      className="p-5 rounded-2xl mb-4"
      style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.18)' }}
      data-testid={`match-review-${review.id}`}
    >
      <span className="text-xs tracking-[0.15em] block mb-3" style={{ ...sans, color: '#C9A84C' }}>WHEN YOU'RE READY</span>
      <p className="text-sm leading-relaxed mb-2" style={{ ...serif, color: '#1A1A2E' }}>
        A part called “{name}” came up {fromSage ? 'while talking with Sage' : 'in a realm'}.
        It may be the same as {matches.map((m) => `“${m.part_name}”`).join(' or ')}, already on your Council — or someone new. Only you know.
      </p>
      {review.candidate?.context && (
        <p className="text-xs italic mb-3" style={{ ...sans, color: '#6B6878' }}>“{review.candidate.context}”</p>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        {matches.map((m) => (
          <button
            key={m.id}
            disabled={busy}
            onClick={() => resolve('same_part', m.id)}
            className="px-3 py-2 rounded-full text-xs transition-all duration-200 active:scale-[0.97]"
            style={{ ...sans, background: 'rgba(138,158,143,0.18)', color: '#a9c0ae', border: '1px solid rgba(138,158,143,0.3)' }}
            data-testid={`review-same-as-${m.part_name}`}
          >
            Same as “{m.part_name}”
          </button>
        ))}
        <button
          disabled={busy}
          onClick={() => resolve('different_part')}
          className="px-3 py-2 rounded-full text-xs transition-all duration-200 active:scale-[0.97]"
          style={{ ...sans, background: '#F3EEE8', color: '#4A4A5A' }}
          data-testid="review-different-part"
        >
          A different part
        </button>
        <button
          disabled={busy}
          onClick={() => resolve('set_aside')}
          className="px-3 py-2 rounded-full text-xs transition-all duration-200 active:scale-[0.97]"
          style={{ ...sans, background: 'transparent', color: '#8A8798' }}
          data-testid="review-set-aside"
        >
          Set aside for now
        </button>
      </div>
    </div>
  );
};

const CompanionEntries = ({ companion, entries, loading, onBack }) => {
  const isConversation = companion.status === 'named_in_conversation';

  const renderEntries = () => {
    if (loading) {
      return <p className="text-sm text-center" style={{ ...sans, color: '#8A8798' }}>Gathering memories…</p>;
    }
    if (entries.length === 0) {
      return (
        <p className="text-sm text-center leading-relaxed py-6" style={{ ...sans, color: '#8A8798' }} data-testid="companion-no-entries">
          {isConversation
            ? `No realm reflections yet. If you'd like, you can invite ${companion.part_name} along on a quest — they'll appear as a companion you can choose.`
            : 'No reflections together yet.'}
        </p>
      );
    }
    return (
      <div className="space-y-4">
        {entries.map((e) => (
          <div key={e.id} className="p-5 rounded-2xl" style={{ background: '#F7F1EE' }} data-testid={`companion-entry-${e.id}`}>
            <p className="text-xs mb-2" style={{ ...sans, color: '#72826F' }}>{e.quest_title || 'A reflection'}</p>
            <p className="text-sm leading-relaxed mb-3 italic" style={{ ...serif, color: '#9C685D' }}>{e.prompt_text}</p>
            <p className="text-sm leading-relaxed" style={{ ...sans, color: '#2E2E42' }}>{e.user_response}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="companion-entries-view">
      <button onClick={onBack} className="flex items-center gap-2 mb-6 text-xs tracking-[0.1em]" style={{ ...sans, color: '#6B6878' }} data-testid="companion-entries-back">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 18l-6-6 6-6" /></svg>
        BACK TO COUNCIL
      </button>
      <div className="flex items-center gap-4 mb-6">
        <CompanionAvatar name={companion.part_name} avatarSeed={companion.avatar_seed} size={56} muted={isConversation} />
        <div>
          <h2 className="text-lg" style={{ ...serif, color: '#1A1A2E' }}>{companion.part_name}</h2>
          <p className="text-xs" style={{ ...sans, color: isConversation ? 'rgba(169,192,174,0.65)' : '#8A6A61' }}>
            {isConversation
              ? `Named in conversation with Sage${companion.identified_at ? ` · ${companion.identified_at.slice(0, 10)}` : ''}`
              : `Met in ${REALM_NAMES[companion.realm_met_in] || `Realm ${companion.realm_met_in}`}`}
          </p>
        </div>
      </div>

      {companion.source_context && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#F9F4F0' }} data-testid="companion-source-context">
          <p className="text-xs mb-1" style={{ ...sans, color: '#72826F' }}>How this part first showed up</p>
          <p className="text-sm italic leading-relaxed" style={{ ...serif, color: '#3C3C50' }}>“{companion.source_context}”</p>
        </div>
      )}
      {companion.traits?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="companion-traits">
          {companion.traits.map((t) => (
            <span key={t} className="px-3 py-1 rounded-full text-xs" style={{ ...sans, background: '#F7F1EE', color: '#4A4A5A' }}>{t}</span>
          ))}
        </div>
      )}

      {renderEntries()}
    </div>
  );
};

export default function CouncilRosterPage() {
  const navigate = useNavigate();
  const { members, reviews, loading, selected, entries, entriesLoading, openCompanion, closeCompanion, resolveReview } = useCouncil();

  const renderRoster = () => {
    if (loading) {
      return <p className="text-sm text-center" style={{ ...sans, color: '#8A8798' }}>Gathering…</p>;
    }
    if (members.length === 0) {
      return (
        <div className="text-center py-12" data-testid="council-empty-state">
          <div className="w-12 h-12 rounded-full mx-auto mb-6" style={{ background: 'radial-gradient(circle, rgba(138,158,143,0.35), transparent)' }} />
          <p className="text-base leading-relaxed" style={{ ...serif, color: '#4A4A5A' }}>
            Your Council is waiting to grow.
          </p>
          <p className="text-sm mt-3" style={{ ...sans, color: '#8A6A61' }}>
            As you walk the realms — or name parts of yourself while talking with Sage — they will gather here.
          </p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-4">
        {members.map((m) => (
          <CompanionCard
            key={m.id}
            name={m.part_name}
            avatarSeed={m.avatar_seed}
            realmMet={m.realm_met_in}
            realmName={REALM_NAMES[m.realm_met_in]}
            status={m.status}
            onTap={() => openCompanion(m)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-16" style={{ background: '#FBF9F6' }} data-testid="council-roster-screen">
      <header className="max-w-md mx-auto px-6 pt-8 pb-2 flex items-center justify-between">
        <button onClick={() => navigate('/inner-world')} className="flex items-center gap-2 text-xs tracking-[0.1em]" style={{ ...sans, color: '#6B6878' }} data-testid="council-back-button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 18l-6-6 6-6" /></svg>
          THE MAP
        </button>
        <RestAtShoreButton />
      </header>

      <main className="max-w-md mx-auto px-6 pt-6">
        {selected ? (
          <CompanionEntries companion={selected} entries={entries} loading={entriesLoading} onBack={closeCompanion} />
        ) : (
          <>
            <div className="text-center mb-10">
              <span className="text-xs tracking-[0.2em] block mb-3" style={{ ...sans, color: '#C9A84C' }}>YOUR COUNCIL</span>
              <p className="text-base leading-relaxed" style={{ ...serif, color: '#4A4A5A' }}>
                Every part of you that you've met and named — in the realms and in conversation — gathered in one place.
              </p>
            </div>

            {reviews.length > 0 && (
              <div className="mb-8" data-testid="council-reviews-section">
                {reviews.map((r) => (
                  <MatchReviewCard key={r.id} review={r} onResolve={resolveReview} />
                ))}
              </div>
            )}

            {renderRoster()}
          </>
        )}
      </main>
    </div>
  );
}
